import { useEffect, useRef } from "react";

/**
 * Global cursor-following soft blob, modeled on inkwell.tech's `.mouse-blob`.
 *
 * inkwell uses a 20rem white-ish radial-gradient that tracks the cursor on
 * a `position: fixed` element with `mix-blend-mode: soft-light` (or screen).
 * On a dark theme like ours we use `screen` blend with a teal tint so it
 * reads as a subtle illumination across all sections.
 */
export function InkwellMouseBlob() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    let tx = window.innerWidth / 2;
    let ty = window.innerHeight / 2;
    let cx = tx;
    let cy = ty;

    const onMove = (e: MouseEvent) => {
      tx = e.clientX;
      ty = e.clientY;
    };
    const tick = () => {
      // smooth follow (lerp 12%)
      cx += (tx - cx) * 0.12;
      cy += (ty - cy) * 0.12;
      el.style.transform = `translate3d(${cx - 160}px, ${cy - 160}px, 0)`;
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove);
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        width: "20rem",
        height: "20rem",
        borderRadius: "50%",
        pointerEvents: "none",
        zIndex: 30,
        mixBlendMode: "screen",
        background:
          "radial-gradient(circle, rgba(64,176,194,0.18) 0%, rgba(64,176,194,0.10) 35%, transparent 70%)",
        filter: "blur(40px)",
        willChange: "transform",
      }}
    />
  );
}
